<script setup lang="ts">
// TODO for activiteitenkalender:
// - Frequency type: make filtering by Meerdaagse activiteiten possible 👀
// - Store stuff with useState 👀

import { useLowerCase, SearchViewMode } from '#imports'

const { t } = useI18n({ useScope: 'local' })

// TODO: there's an issue with this: it doesn't work with ClientOnly
const { hierarchicalActivityTypes, fetch } = await useActivityTypes(VacancyType.Activity)
onMounted(() => fetch())

const { audiences } = await useAudiences(VacancyType.Activity)
const { ageGroups } = await useAgeGroups()
// Not a very clean solution, but it works.
const ageGroupsExcludingAllOption = computed(() => ageGroups.value.filter(a => !a.name.includes('All')))

const frequencies = useActivityFrequencies()
const timeOfDayValues = Object.values(TimeOfDay)

const { data: localities } = await fetchLocalities()
const enableLocalityFilter = computed(() => !!localities.value?.length)
const localityType: 'village' | 'neighborhood' = 'village'

const router = useRouter()

const activeViewMode = useState<SearchViewMode>('searchMapViewMode', () => SearchViewMode.list)

const { isMobile } = useDevice()
// const isMobile = true

const showMobileSidebar = ref(false)
const showSidebar = computed(() => {
  // Always show on tablet/desktop
  if (!isMobile) return true
  // On mobile, controlled by toggle
  return showMobileSidebar.value
})
const showCTA = computed(() => false)

const search = useState('activitySearch', () => '')
const selectedFrequencies = useState<string[]>('activitySelectedFrequencies', () => [])
const selectedActivityTypeIds = useState<id[]>('activitySelectedActivityTypeIds', () => [])
const selectedAudienceIds = useState<id[]>('activitySelectedAudienceIds', () => [])
const selectedAgeGroupIds = useState<id[]>('activitySelectedAgeGroupIds', () => [])

const dates = useState<string[]>('activityDates', () => [])
const timeOfDay = useState<string[]>('activityTimeOfDay', () => [])
const freeActivities = useState('activityFreeActivities', () => false)
const heumenSjorsSportief = useState('activityHeumenSjorsSportief', () => false)
const heumenMaxVitaal = useState('activityHeumenMaxVitaal', () => false)

const { isHeumen } = await useCurrentCenter()

const filterByTags = computed(() => {
  const tags = []
  if (heumenSjorsSportief.value) {
    tags.push('sjors_sportief')
  }
  if (heumenMaxVitaal.value) {
    tags.push('max_vitaal')
  }
  if (freeActivities.value) {
    tags.push('free')
  }
  return tags
})

const postcode = useState('activityPostcode', () => '')
const distance = useState<number>('activityDistance', () => 1)
const isFilteringByLocation = computed(
  // We need at least 6 characters for a valid postcode
  // 4 chars is not supported (yet)
  () => !!postcode.value && postcode.value.length >= 6,
)
const localityId = useState<id | undefined>('activityLocalityId', () => undefined)
const fromHome = useState('activityFromHome', () => false)

const perPage = ref(showCTA.value ? 9 : 10)

const perPageForCurrentViewMode = computed(() => activeViewMode.value === SearchViewMode.map ? 250 : perPage.value)

const page = useState('activityPage', () => 1)
const sortBy = useState('activitySortBy', () => 'latest')

const searchWordFilter = 'filter[title_or_full_description_or_organization_name_cont]'

// Debounce the search input
const searchDebounced = refDebounced(search, 150)

const searchOptionsQuery = computed(() => {
  // Determine if we're filtering for multi-day activities
  const includesMultiDay = selectedFrequencies.value.includes(Frequency.MultipleDays)
  // Other frequency filters (once, regularly)
  const otherFrequencies = selectedFrequencies.value.filter(f => f !== Frequency.MultipleDays)

  return {
    [searchWordFilter]: searchDebounced.value,
    'filter[vacancy_type_eq]': VacancyType.Activity,
    'filter[localities_id_eq]': localityId.value,
    // If we're filtering by multiple days, we don't want to filter by frequency.
    'filter[frequency_in][]': otherFrequencies.length > 0 ? otherFrequencies : null,
    // Only explicitly filter for single-day when 'once' is selected without multi-day
    'filter[multi_day_eq]': includesMultiDay
      ? 'true'
      : (selectedFrequencies.value.includes(Frequency.Once) ? 'false' : null),
    'filter[locality_id_eq]': localityId.value,
    'filter[tags_in][]': filterByTags.value,
    'filter[age_groups_id_in][]': selectedAgeGroupIds.value,
    'filter[activity_types_id_in][]': selectedActivityTypeIds.value,
    'filter[audiences_id_in][]': selectedAudienceIds.value,
    'filter[dates_in][]': dates.value,
    'filter[time_of_day_in][]': timeOfDay.value,
    'filter[postcode]': isFilteringByLocation.value ? postcode.value : null,
    'filter[distance]': isFilteringByLocation.value ? distance.value : null,
    'filter[from_home]': fromHome.value,
  }
})

const query = computed(() => {
  const rawQuery = {
    ...searchOptionsQuery.value,
    ...{
      'public_search': true,
      'filter[status_eq]': 'published',
      'filter[for_meedoen_eq]': false,
      'page[size]': perPageForCurrentViewMode.value,
      'page[number]': page.value,
    },
  }
  return cleanObject(rawQuery)
})

const { data: vacancies, status } = await fetchVacanciesNew(query)

const meta = computed(() => vacancies.value?._meta)
const facets = computed(() => meta.value?.facets)
const dateFacets = computed(() => facets.value?.dates)

const totalItems = computed(() => meta.value?.page?.totalItems)

const noSearchResults = computed(() => totalItems.value === 0)
// If there are any previous search results, we want to show the same amount of placeloaders.
// If there were no previous results, we want to show a full page of placeloaders.
const numberOfPlaceLoaders = computed(() =>
  vacancies.value?.length && vacancies.value?.length > 0 ? vacancies.value.length : perPageForCurrentViewMode.value,
)

// Enable once we have location search working
const sortEnabled = false

// Reset the page when the search options or perPage changes
watch([searchOptionsQuery, perPageForCurrentViewMode], () => {
  page.value = 1
})

// Update the URL query when the filters change
watch(
  query,
  (query) => {
    console.log(`query`, query)
    router.push({ query })
  },
  { deep: true },
)

const pluralTypeName = t(`activities`)

// Map vacancies to items for the map
const mapItems = computed(() => {
  return (vacancies.value || [])?.map(v => ({
    ...v,
    description: v.fullDescription,
    latitude: v.address?.latitude,
    longitude: v.address?.longitude,
    formattedAddress: v.address?.formattedAddress,
    to: `/vacancies/${v.id}`,
  }))
})

</script>

<template>
  <div class="mb-6 space-y-4">
    <!-- Search bar -->
    <div class="relative">
      <BaseCard
        shape="curved"
        class="ptablet:py-6 ptablet:px-4 ptablet:grid ptablet:grid-cols-12 ltablet:divide-x divide-muted-200 dark:divide-muted-700 flex w-full flex-col items-center py-2 sm:flex-row sm:py-0 lg:divide-x"
      >
        <div
          class="ptablet:ps-4 ptablet:col-span-6 w-full py-2 pe-4 ps-4 sm:w-auto sm:grow sm:ps-2"
        >
          <BaseInput
            v-model.trim="search"
            shape="curved"
            icon="lucide:search"
            :placeholder="t('searchInputPlaceholder')"
          />
        </div>
      </BaseCard>
    </div>

    <BaseButton
      v-if="isMobile"
      class="w-full"
      @click="showMobileSidebar = !showMobileSidebar"
    >
      <Icon name="lucide:filter" class="size-5" />
      {{ showMobileSidebar ? 'Verberg filters' : 'Toon filters' }}
    </BaseButton>

    <!-- Grid -->
    <div class="grid grid-cols-12 gap-6">
      <!-- Sidebar column -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="transform -translate-x-full opacity-0"
        enter-to-class="transform translate-x-0 opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="transform translate-x-0 opacity-100"
        leave-to-class="transform -translate-x-full opacity-0"
      >
        <div
          v-if="showSidebar"
          :class="isMobile ? ['fixed h-full w-full overflow-y-auto inset-0 z-40 px-4 pt-20 pb-24'] : null"
          class="
            ptablet:col-span-4 ltablet:col-span-4 dark:bg-muted-900 col-span-12
            bg-white md:relative md:overflow-visible md:bg-transparent lg:col-span-3 md:dark:bg-transparent
          "
        >
          <nav v-if="isMobile" class="bg-muted-100 fixed left-0 top-0 z-50 h-14 w-full">
            <div class="flex h-full items-center justify-between px-4">
              <BaseHeading
                as="h3"
                size="lg"
                weight="light"
                lead="tight"
                class="text-muted-800 dark:text-white"
              >
                {{ t('filterTitle') }}
              </BaseHeading>

              <button
                class=""
                @click="showMobileSidebar = false"
              >
                <Icon name="lucide:x" class="size-6" />
              </button>
            </div>
          </nav>

          <div class="w-full">
            <div class="space-y-8">
              <BaseCard class="flex flex-col gap-y-4 px-6 py-5">
                <BaseHeading
                  as="h3"
                  weight="medium"
                  size="md"
                >
                  {{ t('location') }}
                </BaseHeading>

                <FormListField
                  v-if="enableLocalityFilter"
                  v-model="localityId"
                  name="localityId"
                  :items="localities"
                  :label="`Zoek op ${useLowerCase(t(`neighborhoodTitle.${localityType}`))}`"
                  :include-null="true"
                  :label-factory="(l: Locality) => l?.name || 'Alle dorpen'"
                  @on-select="(l: Locality) => localityId = l?.id"
                />

                <VacancySearchPostcodeInput
                  v-model="postcode"
                  :label="enableLocalityFilter ? t('orPostcode') : undefined"
                />

                <Transition
                  enter-active-class="transition duration-500 ease-in"
                  enter-from-class="max-h-0 opacity-0"
                  enter-to-class="max-h-[150px] opacity-100"
                  leave-active-class="transition duration-100 ease-out"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <div
                    v-show="isFilteringByLocation"
                    class="flex flex-col gap-y-4"
                  >
                    <BaseSlider
                      v-model="distance"
                      :min="1"
                      :max="25"
                      :format="(value: any) => `${parseInt(value)} km`"
                      :label="t('distance')"
                    />
                  </div>
                </Transition>

                <BaseSwitchThin
                  v-model="fromHome"
                  :label="t('fromHome')"
                  class="mt-4"
                />
              </BaseCard>

              <VacancySearchSidebarCard
                v-model="selectedActivityTypeIds"
                :title="t('activityType')"
                :items="hierarchicalActivityTypes"
                :item-counts="facets?.activityTypeIds"
              />

              <VacancySearchSidebarCard
                v-model="selectedFrequencies"
                :title="t('frequency')"
                :items="frequencies"
                :item-counts="facets?.frequencies"
              />

              <!-- "When" card -->
              <BaseCard v-if="dateFacets && dateFacets?.length > 0" class="px-6 py-5">
                <VacancySearchSidebarHeading>{{ t('whenCard.title') }}</VacancySearchSidebarHeading>

                <Datepicker
                  v-model="dates"
                  :multiple-dates="true"
                  :selectable-dates="dateFacets"
                />
              </BaseCard>

              <BaseCard class="px-6 py-5">
                <VacancySearchSidebarHeading>{{ t('timeOfDayCard.title') }}</VacancySearchSidebarHeading>

                <div class="flex flex-col gap-y-4">
                  <BaseCheckbox
                    v-for="timeOfDayValue in timeOfDayValues"
                    :key="timeOfDayValue"
                    v-model="timeOfDay"
                    color="primary"
                    :value="timeOfDayValue"
                    :label="t(`timeOfDayCard.labels.${timeOfDayValue}`)"
                  />
                </div>

                <!-- <pre>{{ timeOfDay }}</pre> -->
              </BaseCard>

              <!-- More search options  -->
              <BaseCard class="px-6 py-5">
                <VacancySearchSidebarHeading>{{ t('moreOptionsCard.title') }}</VacancySearchSidebarHeading>

                <div class="flex flex-col gap-y-4">
                  <BaseCheckbox
                    v-model="freeActivities"
                    color="primary"
                    :label="t('moreOptionsCard.freeActivities')"
                  />

                  <BaseCheckbox
                    v-if="isHeumen"
                    v-model="heumenSjorsSportief"
                    color="primary"
                    :label="t('moreOptionsCard.heumenSjorsSportief')"
                  />

                  <BaseCheckbox
                    v-if="isHeumen"
                    v-model="heumenMaxVitaal"
                    color="primary"
                    :label="t('moreOptionsCard.heumenMaxVitaal')"
                  />
                </div>
              </BaseCard>

              <VacancySearchSidebarCard
                v-model="selectedAgeGroupIds"
                :title="t('ageGroup')"
                :items="ageGroupsExcludingAllOption"
              />

              <VacancySearchSidebarCard
                v-model="selectedAudienceIds"
                :title="t('audience')"
                :items="audiences"
                :item-counts="facets?.audienceIds"
              />

              <div v-if="isMobile" class="bg-primary-500 fixed inset-x-0 bottom-0 p-4 text-white">
                <!-- Hide filters button on mobile -->
                <BaseButton
                  class="mb-4 w-full"
                  @click="showMobileSidebar = !showMobileSidebar"
                >
                  <Icon name="lucide:filter" class="size-5" />
                  <span v-if="totalItems" class="ml-2">Toon {{ totalItems }} resultaten</span>
                  <span v-else class="ml-2">Geen resultaten voor deze filters</span>
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Column -->
      <div
        v-show="!showMobileSidebar"
        :class="{
          'ptablet:col-span-8 ltablet:col-span-8 col-span-12 lg:col-span-9': showSidebar,
          'col-span-full': !showSidebar,
        }"
        class=""
      >
        <div class="my-0 flex justify-between">
          <!-- Title + count -->
          <div class="mb-6 flex w-full flex-col gap-4 sm:mt-0 md:mt-2 md:flex-row md:items-center md:justify-between">
            <div class="">
              <BaseHeading
                as="h3"
                size="lg"
                weight="light"
                lead="tight"
                class="text-muted-800 dark:text-white"
              >
                <span v-if="status == 'pending'">Aan het zoeken...</span>
                <span v-else-if="status == 'success'">{{
                  t('vacancyCount', { count: totalItems, pluralTypeName })
                }}</span>
              </BaseHeading>
              <BaseParagraph size="sm">
                <span class="text-muted-500">
                  {{ t('vacancyCountText', { pluralTypeName }) }}
                </span>
              </BaseParagraph>
            </div>

            <!-- TODO: full width on mobile -->
            <BaseButtonGroup>
              <BaseButtonAction
                v-for="viewMode in Object.values(SearchViewMode)"
                :key="viewMode"
                class="h-9 w-24"
                shape="rounded"
                :color="activeViewMode === viewMode ? 'primary' : 'muted'"
                @click="activeViewMode = viewMode"
              >
                <span class="capitalize">
                  {{ t(`viewMode.${viewMode}`) }}
                </span>
              </BaseButtonAction>
            </BaseButtonGroup>
          </div>

          <!-- Sort filter -->
          <div v-if="sortEnabled" class="flex items-center gap-x-2">
            <label for="sortBy" class="nui-label">{{ t('sortByLabel') }}</label>
            <BaseSelect
              id="sortBy"
              v-model="sortBy"
              label=""
              shape="rounded"
              :classes="{
                wrapper: 'w-40',
              }"
            >
              <option value="latest">
                {{ t('sortBy.latest') }}
              </option>
              <option value="distance">
                {{ t('sortBy.distance') }}
              </option>
            </BaseSelect>
          </div>
        </div>

        <!-- Inner grid -->
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SearchMap
            v-if="activeViewMode === SearchViewMode.map"
            :items="mapItems"
          />

          <template v-else-if="status == 'pending'">
            <VacancySearchPlaceLoadCard
              v-for="index in numberOfPlaceLoaders"
              :key="index"
            />
          </template>

          <NoSearchResult v-else-if="noSearchResults" class="col-span-full" />

          <template v-else>
            <VacancySearchCard
              v-for="vacancy in vacancies"
              :key="vacancy.id"
              :vacancy="vacancy"
              class="col-span-1"
            />

            <!-- Still experimenting with wull with or n-th card grid item -->
            <VacancySearchCallToAction v-if="showCTA" class="col-span-1" />
          </template>
        </div>

        <div v-if="!noSearchResults" class="mt-6">
          <BasePagination
            :total-items="totalItems ?? 0"
            :item-per-page="perPageForCurrentViewMode"
            :current-page="page"
            :no-router="true"
            shape="curved"
            @update:current-page="page = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "activities": "activities",
    "searchInputPlaceholder": "Search keywords",
    "search": "Search",
    "filterTitle": "Refine search",
    "vacancyCount": "Showing {count} activities",
    "vacancyCountText": "These are the matching {pluralTypeName} that we found",
    "location": "Location",
    "distance": "Distance",
    "orPostcode": "Or search by postcode",
    "fromHome": "From home or online",
    "sortByLabel": "Sort by",
    "sortBy": {
      "latest": "Latest",
      "distance": "Distance"
    },
    "frequency": "Frequency",
    "neighborhoodTitle": {
      "village": "Village",
      "neighborhood": "Neighborhood"
    },
    "whenCard": {
      "title": "Search by date"
    },
    "timeOfDayCard": {
      "title": "Search by time of day",
      "labels": {
        "morning": "Morning",
        "afternoon": "Afternoon",
        "evening": "Evening"
      }
    },
    "moreOptionsCard": {
      "title": "More search options",
      "freeActivities": "Free activities",
      "heumenSjorsSportief": "Sjors Sportief & Creatief",
      "heumenMaxVitaal": "Max vitaal"
    },
    "audience": "For whom",
    "ageGroup": "Age group",
    "activityType": "Activity type",
    "viewMode": {
      "list": "Lijst",
      "map": "Kaart"
    }
  },
  "nl": {
    "activities": "activiteiten",
    "searchInputPlaceholder": "Zoekwoord",
    "search": "Zoeken",
    "filterTitle": "Verfijn zoekopdracht",
    "vacancyCount": "{count} {pluralTypeName} gevonden",
    "vacancyCountText": "Deze {pluralTypeName} matchen bij je zoekopdracht",
    "location": "Locatie",
    "distance": "Afstand",
    "orPostcode": "Of zoek op postcode",
    "fromHome": "Thuis of online",
    "pluralName": {
      "volunteering": "vacatures",
      "workshop": "workshops",
      "activity": "activiteiten",
      "p2p": "1:1 hulpvragen"
    },
    "sortByLabel": "Sorteer op",
    "sortBy": {
      "latest": "Nieuwste",
      "distance": "Afstand"
    },
    "neighborhoodTitle": {
      "village": "Dorp",
      "neighborhood": "Wijk"
    },
    "frequency": "Frequentie",
    "whenCard": {
      "title": "Zoek op datum"
    },
    "timeOfDayCard": {
      "title": "Zoek op dagdeel",
      "labels": {
        "morning": "Ochtend",
        "afternoon": "Middag",
        "evening": "Avond"
      }
    },
    "moreOptionsCard": {
      "title": "Meer zoekopties",
      "freeActivities": "Gratis activiteiten",
      "heumenSjorsSportief": "Sjors Sportief & Creatief",
      "heumenMaxVitaal": "Max vitaal"
    },
    "audience": "Voor wie",
    "ageGroup": "Leeftijdsgroep",
    "activityType": "Soort activiteit",
    "viewMode": {
      "list": "Lijst",
      "map": "Kaart"
    }
  }
}
</i18n>
